/**
 * @file RowActionsMenu.tsx
 * @description Меню «⋯» действий по строке/карточке (phase19.23 этап A, п. 6
 * чек-листа A6).
 *
 * Действия по строке живут только здесь: в списке остаются данные, а не ряд
 * кнопок, который на 360px уезжает за край. Набор пунктов — массив-реестр,
 * новое действие = запись, а не ветка в разметке.
 *
 * Тач-цель — квадрат 48px на всех ширинах; `size="wms"` поднимает её до 56px
 * на `<lg` (складской экран, палец в перчатке).
 *
 * Подача разная по правилам mobile-first: на ≥lg выпадающее меню, на <lg —
 * нижняя шторка с кнопками ≥56px. Обе ветки рисуются всегда и скрываются
 * классами: медиазапрос в JS давал бы мигание при первом рендере.
 *
 * ```tsx
 * <RowActionsMenu
 *   title="Действия с ячейкой"
 *   actions={[
 *     { key: "contents", label: "Содержимое", icon: Boxes, run: open },
 *     { key: "archive", label: "В архив", icon: Archive, danger: true, run: archive },
 *   ]}
 * />
 * ```
 */

import { MoreHorizontal } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useState } from "react";

import { cn } from "@/lib/utils";

import { Button } from "./button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./sheet";

/** Пункт меню. Деструктивное действие ставим последним — правило A6. */
export interface RowAction {
  key: string;
  label: string;
  icon: LucideIcon;
  danger?: boolean;
  disabled?: boolean;
  /** Пояснение под пунктом — почему он выключен («Все 4 начертания уже загружены»). */
  hint?: string;
  run: () => void;
}

interface Props {
  actions: RowAction[];
  /** Заголовок мобильной шторки: «Действия с ячейкой». */
  title: string;
  /** Подпись кнопки для скринридера. */
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * `wms` — тач-цель 56px на `<lg` (складские экраны), `default` — 48px.
   * `compact` (phase19.31, О4) — размер даёт тема: 32px с 1024, 48px до 1024,
   * 56px у кладовщика до 1024 (страницы на Commora, без своих высот).
   */
  size?: "default" | "wms" | "compact";
}

export function RowActionsMenu({
  actions,
  title,
  ariaLabel = "Другие действия",
  disabled = false,
  size = "default",
}: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  // Тач-цель «⋯» задаём квадратом, а не только `min-h`: с `min-h` ширину
  // определяли паддинги варианта, и на телефоне кнопка выходила 32px в узкую
  // сторону (находка QA phase19.24 этап 0). На `lg` — 48px, на складских
  // экранах на `<lg` — 56px: палец в перчатке (правила warehouse-UI).
  // `compact`: без классов размера — высоту и ширину задаёт тема по
  // `data-size="icon-sm"` (--control-compact-h), пункты шторки — по теме.
  const compact = size === "compact";
  const triggerSize = compact ? "icon-sm" : "icon";
  const desktopTriggerCls = compact ? undefined : "size-12";
  const mobileTriggerCls = compact ? undefined : size === "wms" ? "size-14" : "size-12";
  const sheetItemCls = compact ? "w-full" : "min-h-[56px] w-full";
  if (actions.length === 0) return null;

  return (
    <>
      <div className="hidden lg:block">
        <DropdownMenu>
          <DropdownMenuTrigger
            render={(props) => (
              <Button
                {...props}
                variant="outline"
                size={triggerSize}
                className={desktopTriggerCls}
                disabled={disabled}
                aria-label={ariaLabel}
              >
                <MoreHorizontal className="size-4" />
              </Button>
            )}
          />
          <DropdownMenuContent align="end" sideOffset={6} className="min-w-64 max-w-80">
            {actions.map((action) => (
              <DropdownMenuItem
                key={action.key}
                onClick={action.run}
                disabled={action.disabled}
                className={action.danger ? "py-2 text-destructive" : "py-2"}
              >
                <action.icon className="size-4" />
                {action.hint ? (
                  <span className="flex min-w-0 flex-col">
                    {action.label}
                    <span className="type-caption whitespace-normal text-on-surface-variant">{action.hint}</span>
                  </span>
                ) : (
                  action.label
                )}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="lg:hidden">
        <Button
          variant="outline"
          size={triggerSize}
          className={mobileTriggerCls}
          disabled={disabled}
          onClick={() => setSheetOpen(true)}
          aria-label={ariaLabel}
        >
          <MoreHorizontal className="size-5" />
        </Button>
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetContent side="bottom" className="gap-3 pb-6">
            <SheetHeader>
              <SheetTitle>{title}</SheetTitle>
            </SheetHeader>
            {actions.map((action) => (
              <Button
                key={action.key}
                variant="outline"
                disabled={action.disabled}
                // С пояснением кнопка растёт по тексту: тема задаёт только минимальную высоту.
                className={cn(sheetItemCls, action.hint && "h-auto py-2", action.danger && "text-destructive")}
                onClick={() => {
                  setSheetOpen(false);
                  action.run();
                }}
              >
                <action.icon className="size-5" />
                <span className="ml-1.5 flex min-w-0 flex-col text-left">
                  {action.label}
                  {action.hint ? <span className="type-caption whitespace-normal text-on-surface-variant">{action.hint}</span> : null}
                </span>
              </Button>
            ))}
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
